import { useControllableState } from '@rn-primitives/hooks';
import clsx from 'clsx';
import {
  forwardRef,
  ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  InputModeOptions,
  KeyboardTypeOptions,
  Pressable,
  TextInput,
  TextStyle,
  View,
} from 'react-native';
import { HideIcon } from '../../Icons/HideIcon';
import { ShowIcon } from '../../Icons/ShowIcon';
import { useTheme } from '../../lib/useTheme';
import { cn } from '../../lib/utils';
import { mapFontWeights } from '../Text/helper';
import { KeyboardOverlay } from './KeyboardOverlay/KeyboardOverlay/KeyboardOverlay';

interface TextFieldProps {
  style?: TextStyle;
  className?: string;
  value?: string;
  onValueChanged?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  keyboardType?: KeyboardTypeOptions;
  isFocused?: boolean;
  maxLength?: number;
  placeholder: string;
  readOnly?: boolean;
  fontSize?: number;
  fontWeight?: TextStyle['fontWeight'];
  autoCapitalize?: boolean;
  lineHeigth?: number;
  inputMode?: InputModeOptions;
  keyboardOverlay?: ReactNode;
  isPassword?: boolean;
  autoCorrect?: boolean;
  fullWidth?: boolean;
  inputIcon?: React.ReactNode;
  onIconPress?: () => void;
}

export interface TextFieldRef {
  focus: () => void;
  blur: () => void;
}

export const TextField = forwardRef<TextFieldRef, TextFieldProps>(
  (
    {
      style,
      className,
      value,
      keyboardType = 'default',
      onValueChanged,
      onFocus,
      onBlur,
      isFocused = false,
      maxLength,
      placeholder,
      readOnly = false,
      fontSize = 24,
      fontWeight = 600,
      inputMode,
      keyboardOverlay,
      lineHeigth,
      isPassword = false,
      autoCapitalize = false,
      autoCorrect = false,
      fullWidth = false,
      inputIcon,
      onIconPress,
    },
    ref,
  ) => {
    const theme = useTheme();
    const inputAccessoryViewID = useId();
    const [focused, setIsFocused] = useState(false);
    const inputRef = useRef<TextInput>(null);
    const [showPassword, setShowPassword] = useState(false);

    const [internalValue, handleValueChanged] = useControllableState({
      prop: value,
      onChange: onValueChanged,
      defaultProp: '',
    });

    function handleChangeText(text: string) {
      // The autoCapitalize prop only works on react native, not web. Thats why we need to manually handle it here
      if (autoCapitalize) {
        handleValueChanged(text.toUpperCase());
      } else {
        handleValueChanged(text);
      }
    }

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
    }));

    // TODO: rework this
    let customStyle: TextStyle = {
      textAlignVertical: 'center',
      fontFamily: 'Bozon',
      fontSize: fontSize,
      fontWeight: fontWeight,
      lineHeight: lineHeigth,
      // Give the password input a little more space between the dots
      letterSpacing: isPassword && !showPassword ? 3 : undefined,
      ...style,
    };

    customStyle = mapFontWeights(customStyle);

    useEffect(() => {
      if (isFocused) {
        inputRef?.current?.focus();
      }
    }, [isFocused]);

    return (
      <>
        <View
          className={clsx(
            'relative flex-row items-center',
            fullWidth && 'w-full',
          )}>
          <TextInput
            //This is a temporary solution for IOS flickering issue during email input
            textContentType="oneTimeCode"
            secureTextEntry={isPassword && !showPassword}
            inputMode={inputMode}
            style={customStyle}
            placeholder={placeholder}
            autoCapitalize={autoCapitalize ? 'characters' : 'none'}
            autoCorrect={autoCorrect}
            placeholderTextColor={theme.neutral[300]}
            cursorColor={theme.primary}
            ref={inputRef}
            keyboardType={keyboardType}
            className={cn('focus:outline-none text-white', className)}
            value={internalValue}
            onChangeText={handleChangeText}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            autoFocus={isFocused}
            maxLength={maxLength}
            readOnly={readOnly}
            editable={!readOnly}
            // This does exist on react native web, but not on react native, it is needed to make the input not focusable
            // https://github.com/VeryBuy/react-native-web/commit/d6267193ec226166d6ddc5eed7b70d8b5590ee50
            // @ts-ignore
            disabled={readOnly}
            inputAccessoryViewID={
              keyboardOverlay != null ? inputAccessoryViewID : undefined
            }
          />
          {isPassword && (
            <Pressable
              className="absolute right-3"
              onPress={() => setShowPassword(!showPassword)}>
              {showPassword ? (
                //temporary fix for stroke issue
                <HideIcon stroke="#d4d4d4" />
              ) : (
                <ShowIcon stroke="#d4d4d4" />
              )}
            </Pressable>
          )}
          {inputIcon && (
            <Pressable
              className="absolute right-3"
              onPress={() => onIconPress?.()}>
              {inputIcon}
            </Pressable>
          )}
        </View>
        {keyboardOverlay && (
          <KeyboardOverlay
            inputAccessoryViewID={inputAccessoryViewID}
            inputFocused={isFocused}>
            {keyboardOverlay}
          </KeyboardOverlay>
        )}
      </>
    );
  },
);
